import React, {
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from "react";
import SimpleObservable from "../helper/simple-observable";
import ToggleableFade from "./toggleable-fade";
import * as styles from "./velavu-modal.module.scss";
import { classArr } from "../helper/style-helper";

export interface TryResolveData<T> {
	resolve: T | undefined; //The resolution data
	cancel: VoidFunction; //Cancels this resolve attempt
	initiateResolve: (data?: T) => void; //Closes this dialog
}

export type ModalResolver<T> = (value?: T) => void;

interface VelavuModalBuilder<T> {
	(
		resolve: ModalResolver<T>,
		onTryResolve: SimpleObservable<TryResolveData<T>>,
	): React.ReactNode;
}

interface VelavuModalRegistered<T> {
	builder: VelavuModalBuilder<T>;
	id: number;
	promiseResolve: (value?: T | undefined) => void;
	removeState: boolean;
	pinToTop: boolean;
	onTryResolve: SimpleObservable<TryResolveData<T>>;
}

export interface ModalContextProps {
	pushModal: <T>(
		builder: VelavuModalBuilder<T>,
		config?: ModalConfig,
	) => Promise<T | undefined>;
	pushModalWithResolver: <T>(
		builder: VelavuModalBuilder<T>,
		config?: ModalConfig,
	) => [Promise<T | undefined>, ModalResolver<T>];
}

interface ModalConfig {
	//Whether to pin this modal to the top
	pinToTop?: boolean;
	isCloseable?: boolean;
}

const VelavuModalContext = React.createContext<ModalContextProps>({
	pushModal() {
		throw new Error("No modal context available");
	},
	pushModalWithResolver() {
		throw new Error("No modal context available");
	},
});

export function useVelavuModal() {
	return useContext(VelavuModalContext);
}

//Place somewhere in the tree to encapsulate modals below it
export function VelavuModalProvider(props: {
	children: React.ReactNode;
	zIndex?: number;
}) {
	const { children, zIndex } = props;

	const modalCounter = useRef(0);
	const [modals, setModals] = useState<VelavuModalRegistered<any>[]>([]);

	const removeModal = useCallback(
		<T,>(modal: VelavuModalRegistered<T>) => {
			setModals((modals) => {
				//Finding the modal index
				const i = modals.indexOf(modal);
				if (i === -1) {
					return modals;
				}

				const newModals = [...modals];
				newModals.splice(i, 1);
				return newModals;
			});
		},
		[setModals],
	);

	const animateRemoveModal = useCallback(
		<T,>(modal: VelavuModalRegistered<T>) => {
			setModals((modals) => {
				//Finding the modal index
				const i = modals.indexOf(modal);
				if (i === -1) {
					return modals;
				}

				const newModals = [...modals];
				newModals[i] = {
					...newModals[i],
					removeState: true,
				};
				return newModals;
			});
		},
		[setModals],
	);

	const resolveModal = useCallback(
		<T,>(modal: VelavuModalRegistered<T>, value?: T) => {
			modal.promiseResolve(value);
			animateRemoveModal(modal);
		},
		[animateRemoveModal],
	);

	const addModal = useCallback(
		<T,>(
			builder: VelavuModalBuilder<T>,
			config?: ModalConfig,
		): [Promise<T | undefined>, ModalResolver<T>] => {
			let newModal: VelavuModalRegistered<T>;
			const resolutionPromise = new Promise<T | undefined>((resolve) => {
				newModal = {
					builder: builder,
					id: modalCounter.current++,
					promiseResolve: resolve,
					removeState: false,
					pinToTop: config?.pinToTop ?? false,
					onTryResolve: new SimpleObservable<TryResolveData<T>>(),
				};
			});

			setModals((modals) => modals.concat(newModal));

			const removeModal = () => {
				resolveModal(newModal);
			};

			return [resolutionPromise, removeModal];
		},
		[setModals, resolveModal],
	);

	const addModalReturnPromise = useCallback(
		<T,>(
			builder: VelavuModalBuilder<T>,
			config?: ModalConfig,
		): Promise<T | undefined> => {
			const [promise] = addModal(builder, config);
			return promise;
		},
		[addModal],
	);

	const contextValue = useMemo<ModalContextProps>(
		() => ({
			pushModal: addModalReturnPromise,
			pushModalWithResolver: addModal,
		}),
		[addModalReturnPromise, addModal],
	);

	const modalComponents = useMemo(() => {
		return modals.map((modal) => {
			const tryResolve = (resolve?: unknown | undefined) => {
				//Emitting the update
				let resolveCancelled = false;
				const cancelResolve = () => (resolveCancelled = true);
				const initiateResolve = (resolve: unknown | undefined) =>
					resolveModal(modal, resolve);
				modal.onTryResolve.emit({
					resolve: resolve,
					cancel: cancelResolve,
					initiateResolve: initiateResolve,
				});

				//Resolving the modal
				if (!resolveCancelled) {
					resolveModal(modal, resolve);
				}
			};

			return (
				<ToggleableFade
					key={modal.id}
					visible={!modal.removeState}
					shift
					onFadeOut={() => removeModal(modal)}
					style={{ zIndex: zIndex }}
					className={classArr(
						styles.scrim,
						modal.pinToTop
							? styles.scrimPinned
							: styles.scrimCenter,
					)}
					onClick={(event) => {
						if (event.target === event.currentTarget) {
							tryResolve();
						}
					}}
				>
					{modal.builder(tryResolve, modal.onTryResolve)}
				</ToggleableFade>
			);
		});
	}, [modals, removeModal, resolveModal, zIndex]);

	return (
		<VelavuModalContext value={contextValue}>
			{children}
			{modalComponents}
		</VelavuModalContext>
	);
}
